import { SetStateAction } from "react";
import { makeCygnusApiCall } from "../../../api/api";
import { buildFormFields, FormField, FormRecord, normalizeClientFieldValues, stripSyntheticRuleFields } from "../ClientCreator/ClientCreator.utils";
import { FieldProps } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";

/**
 * Submits updated client form data to the API.
 *
 * @param setClientUpdateApiProgress - Sets loading spinner state
 * @param clientFormFields - List of form fields with label/value
 * @param clientMasterId - ID of the client being edited
 * @param formUrl - API endpoint for form submission
 * @param formId - ID of the form being used
 * @param onSuccess - Callback to check if save was success
 */
export const updateClientData = async (
  setClientUpdateApiProgress: (arg: boolean) => void,
  clientFormFields: FieldProps[],
  clientMasterId: number,
  formUrl: string,
  formId: number,
  sourceSystemCode: string,
  setShowSSNEINModal: (state: boolean) => void,
  setClientsMatchMessage: (message: any) => void,
  setClientsMatchedData: (count: any) => void,
  continueSavingClient: boolean,
  setShowMatchedSSNEINModal: (state: boolean) => void,
  onSuccess?: () => void,
) => {
  setClientUpdateApiProgress(true);
  const visibleFields = normalizeClientFieldValues(stripSyntheticRuleFields(clientFormFields));

  const formFields: FormField[] = buildFormFields(visibleFields);

  const response = await makeCygnusApiCall({
    type: "PUT",
    payload: {
      ClientFormFields: formFields,
      FormId: formId,
      ClientMasterId: clientMasterId,
      SourceSystemCode: sourceSystemCode,
      ContinueSavingClient: continueSavingClient
    },
    url: `/forms/${formUrl}/`,
    show_message: true,
    customized_success_message: "Prospect saved successfully.",
    customized_failure_message:
      "Unable to save your prospect, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occured while saving the prospect details, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
    skip_success_on_condition: (data: any) => data?.data?.match === true,
  });

  setClientUpdateApiProgress(false);

  if (response.success) {
    if(response.data?.match) {
      // run modal
      const message = response.data?.matched_clients_message;
      if(message) {
        setClientsMatchMessage(message)
        setShowSSNEINModal(true)
      } 
      const matchedData = response.data?.matched_data;
      if(matchedData) { 
        setClientsMatchedData(matchedData)
        setShowMatchedSSNEINModal(true)
      }
      
    }
    else {
      onSuccess?.();
    }
    
    // close();
    // DO NOT CLOSE BY DEFAULT SINCE WE HAVE MULTIPLE TABS TO WORK WITH
  }
};

/**
 * Fetches form definition using form ID.
 *
 * @param formId - ID of the form to load
 * @param setFormDetails - Callback to set the form structure
 * @param setFetchFormApiProgress - Sets loading spinner state
 */
export const fetchFormData = async (
  formId: number,
  setFormDetails: { (value: SetStateAction<FormRecord>): void; (arg: any): void },
  setFetchFormApiProgress: {
    (value: SetStateAction<boolean>): void;
    (arg: boolean): void;
  }
) => {
  setFetchFormApiProgress(true);

  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/form/${formId}/`,
    show_message: true,
    customized_success_message: "Fetched form data.",
    customized_failure_message:
      "Unable to fetch your form, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occured while fetching your client, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    setFormDetails(response.data[0]);
  }

  // setFetchFormApiProgress(false);
};

export type ApiResponse<T = any> = {
  success: boolean;
  message?: string;
  data: T[];
};

export const clientResponseRowsToMap = (rows: any[]): Record<string, any> => {
  if (!Array.isArray(rows) || rows.length === 0) return {};

  if (!("FieldCode" in rows[0])) {
    return rows[0] ?? {};
  }

  return rows.reduce((acc, row) => {
    const fieldCode = row?.FieldCode;
    if (fieldCode) {
      acc[fieldCode] = row.Value;
    }
    if (row?.ClientMasterId && acc.ClientMasterId === undefined) {
      acc.ClientMasterId = row.ClientMasterId;
    }
    return acc;
  }, {} as Record<string, any>);
};

/**
 * Fetches client response data for a given client and form.
 *
 * @param clientMasterId - ID of the client
 * @param setClientDetails - Callback to set field values
 * @param setFetchClientApiProgress - Sets loading spinner state
 */

// ClientEditor.service.ts
export const fetchClientData = async (
  clientId: string | number,
  setClientDetails: (v: any) => void,
  setFetchClientApiProgress: (v: boolean) => void,
  isExistingClientSearch?: boolean
): Promise<ApiResponse<any>> => {
  setFetchClientApiProgress(true);

  const payload: any = { ClientMasterId: clientId };
  if (isExistingClientSearch !== undefined) {
    payload.ExistingClientSearch = isExistingClientSearch;
  }

  try {
    const response = await makeCygnusApiCall({
      type: "POST",
      payload: payload,
      url: `/form/response/`,
      show_message: false,
      customized_success_message: "Fetched form data.",
      customized_failure_message: "Unable to fetch your form, please try again.",
      customized_error_message: "Error occured while fetching your client.",
      isblob: false,
      forMDM: true,
    });

    if (response?.success && Array.isArray(response.data) && response.data[0]) {
      const clientDetails = clientResponseRowsToMap(response.data);
      setClientDetails(clientDetails);
      return { ...response, data: [clientDetails] };
    } else {
      setClientDetails(undefined);
    }

    // always return an object with the API shape
    return response ?? { success: false, message: "No response", data: [] };
  } catch (err: any) {
    console.log("ERROR: ", err)
    setClientDetails(undefined);
    return { success: false, message: err?.message || "Request failed", data: [] };
  } finally {
    setFetchClientApiProgress(false);
  }
};
